import { useEffect, useState } from 'react';
import {
  CollectionSequence,
  CollectionTrigger,
  CollectionTriggerCondition,
  CollectionTriggerCreate,
  CollectionTriggerType,
} from '@dill/dill-shared';
import {
  DillModal,
  DillButton,
  DillInput,
  DillSelect,
  DillToggle,
  DillDropdown,
} from '../../../general/Dill';
import { EngagementClaimant } from './useCollectionBuyerEngagements';

const CONDITION_TYPE_OPTIONS: { label: string; value: CollectionTriggerType }[] = [
  { label: 'Aging bucket balance crosses a threshold', value: 'AGING_BUCKET_THRESHOLD' },
  { label: 'Invoice(s) past due by N days', value: 'DAYS_PAST_DUE' },
];

const BUCKET_OPTIONS = [
  { label: '1-30 days past due', value: '1to30' },
  { label: '31-60 days past due', value: '31to60' },
  { label: '61-90 days past due', value: '61to90' },
  { label: '90+ days past due', value: 'over90' },
];

const BUCKET_LABELS: Record<string, string> = {
  '1to30': '1-30 day',
  '31to60': '31-60 day',
  '61to90': '61-90 day',
  over90: '90+ day',
};

const describeCondition = (condition: CollectionTriggerCondition): string => {
  if (condition.type === 'AGING_BUCKET_THRESHOLD') {
    return `a customer's ${BUCKET_LABELS[condition.bucket]} past-due balance reaches $${(
      condition.amountThreshold || 0
    ).toLocaleString()} or more`;
  }
  return `a customer has any invoice ${condition.days || 0}+ days past due`;
};

const defaultCondition = (type: CollectionTriggerType): CollectionTriggerCondition => {
  if (type === 'AGING_BUCKET_THRESHOLD') {
    return { type, bucket: '31to60', amountThreshold: 1000 };
  }
  return { type, days: 30 };
};

const RequiredMark = () => <span className="text-red-500">*</span>;

interface CollectionTriggerFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CollectionTriggerCreate) => void;
  initialData?: CollectionTrigger | null;
  isLoading?: boolean;
  errorMessage?: string | null;
  sequences: CollectionSequence[];
  supplierBuyers: { id: string; name?: string | null }[];
  // When opened from a specific customer's page, pre-fills the target list with this one selected.
  defaultBuyerId?: string;
  isBuyerAvailable: (buyerId: string, ignoreClaimant?: EngagementClaimant) => boolean;
}

export const CollectionTriggerFormModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading,
  errorMessage,
  sequences,
  supplierBuyers,
  defaultBuyerId,
  isBuyerAvailable,
}: CollectionTriggerFormModalProps) => {
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [condition, setCondition] = useState<CollectionTriggerCondition>(
    defaultCondition('AGING_BUCKET_THRESHOLD')
  );
  const [targetBuyerIds, setTargetBuyerIds] = useState<string[]>([]);
  const [sequenceId, setSequenceId] = useState<string>('');
  // Only start showing per-field validation errors once the user has tried to submit once - so an
  // empty form doesn't greet them with a wall of red before they've typed anything.
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHasAttemptedSubmit(false);
    if (initialData) {
      setName(initialData.name);
      setIsActive(initialData.isActive);
      setCondition(initialData.condition);
      setTargetBuyerIds(initialData.targetBuyerIds);
      setSequenceId(initialData.sequenceId);
    } else {
      setName('');
      setIsActive(true);
      setCondition(defaultCondition('AGING_BUCKET_THRESHOLD'));
      setTargetBuyerIds(defaultBuyerId ? [defaultBuyerId] : []);
      setSequenceId(sequences[0]?.id ?? '');
    }
  }, [open, initialData, defaultBuyerId, sequences]);

  const ignoreClaimant: EngagementClaimant | undefined = initialData
    ? { type: 'TRIGGER_TARGET', id: initialData.id }
    : undefined;

  // Belt-and-suspenders alongside the engagement-map check below: read every sequence's own
  // `buyerIds` directly, so a buyer who's genuinely listed there is caught even if their
  // `collectionBuyerEngagements` doc is somehow missing or hasn't caught up yet - the mutual-
  // exclusion rule shouldn't depend on that index always being perfectly in sync.
  const buyerIdsInAnySequence = new Set(sequences.flatMap((s) => s.buyerIds));
  const isBuyerTrulyAvailable = (buyerId: string) =>
    isBuyerAvailable(buyerId, ignoreClaimant) && !buyerIdsInAnySequence.has(buyerId);

  // Buyers already engaged elsewhere (a different trigger, or any active sequence enrollment) are
  // left out of the picker entirely, so this trigger's target list can never violate the global
  // mutual-exclusion rule.
  const availableBuyers = supplierBuyers.filter((b) => isBuyerTrulyAvailable(b.id));
  const unavailableBuyers = supplierBuyers.filter((b) => !isBuyerTrulyAvailable(b.id));
  const buyerOptions = availableBuyers.map((b) => ({
    label: b.name || 'Unnamed customer',
    value: b.id,
  }));

  // Guards against a currently-selected customer having become unavailable since the form loaded
  // (e.g. someone else just enrolled them into a sequence in another tab/session) - the picker
  // only filters its own options list, so a stale selection carried over from `initialData` needs
  // its own check rather than relying on the picker to catch it.
  const unavailableSelectedBuyerIds = targetBuyerIds.filter((id) => !isBuyerTrulyAvailable(id));

  const sequenceOptions = sequences.map((s) => ({ label: s.name, value: s.id }));

  const handleConditionTypeChange = (type: string) => {
    setCondition(defaultCondition(type as CollectionTriggerType));
  };

  const isNameValid = name.trim().length > 0;
  const isTargetBuyerIdsValid = targetBuyerIds.length > 0;
  const isConditionValid =
    (condition.type !== 'AGING_BUCKET_THRESHOLD' || condition.amountThreshold > 0) &&
    (condition.type !== 'DAYS_PAST_DUE' || condition.days > 0);
  const isSequenceIdValid = !!sequenceId;
  const isValid =
    isNameValid &&
    isTargetBuyerIdsValid &&
    isConditionValid &&
    isSequenceIdValid &&
    unavailableSelectedBuyerIds.length === 0;

  const showError = (fieldIsValid: boolean) => hasAttemptedSubmit && !fieldIsValid;

  const handleSubmit = () => {
    if (!isValid) {
      setHasAttemptedSubmit(true);
      return;
    }
    onSubmit({
      supplierId: '', // filled in by the caller
      name: name.trim(),
      isActive,
      condition,
      targetBuyerIds,
      sequenceId,
      createdBy: null,
    } as CollectionTriggerCreate);
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title={initialData ? 'Edit collection trigger' : 'New collection trigger'}
      subtitle="Pick who this watches, the condition that fires it, and which sequence it enrolls them into."
      maxWidth="560px"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={onClose} />
          <DillButton
            label={initialData ? 'Save changes' : 'Create trigger'}
            onClick={handleSubmit}
            isDisabled={!isValid}
            isLoading={isLoading}
          />
        </>
      }>
      {errorMessage && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 m-0">
          {errorMessage}
        </p>
      )}

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">
          Trigger name <RequiredMark />
        </label>
        <DillInput
          value={name}
          onChange={setName}
          placeholder="e.g. 31-60 bucket escalation"
          error={showError(isNameValid) ? 'Trigger name is required' : undefined}
        />
        {showError(isNameValid) && (
          <p className="text-[11px] text-red-500 m-0">Trigger name is required</p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">Active</label>
        <DillToggle value={isActive} onChange={setIsActive} />
      </div>

      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold text-gray-700 mb-0.5">
          Target customers <RequiredMark />
        </p>
        <p className="text-xs text-gray-400 mt-0 mb-2">
          Which customers this trigger watches - an explicit list is required, there's no "all
          customers" option.
        </p>
        <DillDropdown
          items={buyerOptions}
          value={null}
          isMulti
          showSelectedItems
          multiValue={targetBuyerIds}
          onMultiSelectChange={(newValue) => setTargetBuyerIds(newValue.map((v) => v.value))}
          placeholder="Search customers…"
          containerClassName="h-[36px]"
          showMinimalDesign
          numberOfItemsToShow={3}
          errorText={showError(isTargetBuyerIdsValid) ? 'Select at least one customer' : ''}
        />
        {unavailableBuyers.length > 0 && (
          <p className="text-[11px] text-gray-400 mt-1 mb-0">
            Not shown - already on another trigger's target list or enrolled in a sequence.
          </p>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold text-gray-700 mb-0.5">
          Condition <RequiredMark />
        </p>
        <p className="text-xs text-gray-400 mt-0 mb-2">
          The condition that enrolls a targeted customer into the sequence below.
        </p>
        <div className="flex flex-col gap-2">
          <DillSelect
            value={condition.type}
            onChange={handleConditionTypeChange}
            options={CONDITION_TYPE_OPTIONS}
          />
          {condition.type === 'AGING_BUCKET_THRESHOLD' && (
            <div>
              <div className="grid grid-cols-2 gap-2">
                <DillSelect
                  value={condition.bucket}
                  onChange={(v) => setCondition({ ...condition, bucket: v as any })}
                  options={BUCKET_OPTIONS}
                />
                <DillInput
                  type="number"
                  value={String(condition.amountThreshold)}
                  onChange={(v) => setCondition({ ...condition, amountThreshold: Number(v) || 0 })}
                  placeholder="Amount threshold ($)"
                  error={showError(isConditionValid) ? 'Enter an amount greater than 0' : undefined}
                />
              </div>
              {showError(isConditionValid) && (
                <p className="text-[11px] text-red-500 mt-1 mb-0">
                  Enter an amount threshold greater than 0
                </p>
              )}
            </div>
          )}
          {condition.type === 'DAYS_PAST_DUE' && (
            <div>
              <DillInput
                type="number"
                value={String(condition.days)}
                onChange={(v) => setCondition({ ...condition, days: Number(v) || 0 })}
                placeholder="Days past due"
                error={
                  showError(isConditionValid) ? 'Enter a number of days greater than 0' : undefined
                }
              />
              {showError(isConditionValid) && (
                <p className="text-[11px] text-red-500 mt-1 mb-0">
                  Enter a number of days greater than 0
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold text-gray-700 mb-0.5">
          Enrolls into <RequiredMark />
        </p>
        <p className="text-xs text-gray-400 mt-0 mb-2">
          The Collection Sequence a matching customer is enrolled into when this trigger fires.
        </p>
        {sequences.length === 0 ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 m-0">
            Create a Collection Sequence first, then come back here to assign it.
          </p>
        ) : (
          <DillSelect
            value={sequenceId}
            onChange={setSequenceId}
            options={sequenceOptions}
            placeholder="Choose a sequence"
            error={showError(isSequenceIdValid) ? 'Choose a sequence' : undefined}
          />
        )}
      </div>

      {sequenceId && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-400 m-0 mb-1">
            In plain English
          </p>
          <p className="text-xs text-blue-800 m-0 leading-relaxed">
            When {describeCondition(condition)}, enroll them in{' '}
            <span className="font-medium">
              {sequences.find((s) => s.id === sequenceId)?.name || 'the selected sequence'}
            </span>
            .
          </p>
        </div>
      )}
    </DillModal>
  );
};

export default CollectionTriggerFormModal;
